import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CalendarRange, Database, Download, Eye, Globe, LucideAngularModule, RefreshCcw, X } from 'lucide-angular';
import { Vendor } from '../../../core/models/domain.models';
import { ApiService } from '../../../core/services/api.service';
import { VendorsService } from '../../vendor/services/vendors.service';
import { ToastService } from '../../../shared/ui/toast.service';

type GridIndiaScheduleSnapshot = {
  id: string;
  report_key: string;
  region: string;
  schedule_date: string;
  source_url: string;
  request_method: string;
  row_count: number;
  error_message?: string | null;
  fetched_at?: string | null;
  last_refetched_at?: string | null;
  created_at: string;
};

type GridIndiaScheduleBlock = {
  block_number: number;
  block_label: string;
  schedule_mw: number;
  source_value: number;
};

type GridIndiaScheduleSnapshotDetail = GridIndiaScheduleSnapshot & {
  blocks: GridIndiaScheduleBlock[];
  raw_payload_text: string;
};

interface ApplicationScheduleApi {
  id: string;
  application_id: string;
  from_date: string;
  to_date: string;
  from_time: string;
  to_time: string;
  quantum?: number | null;
}

interface ApplicationApi {
  id: string;
  application_number: string;
  contract_id: string;
  application_date: string;
  route?: string | null;
  seller_vendor_id?: string | null;
  buyer_vendor_id?: string | null;
  approval_number?: string | null;
  total_quantum?: number | null;
  approved_quantum?: number | string | null;
  schedules?: ApplicationScheduleApi[];
}

type TodayApplicationRow = {
  id: string;
  applicationNumber: string;
  approvalNumber: string;
  sellerName: string;
  buyerName: string;
  scheduledQuantum: number;
  actualQuantum: number;
};

type ApplicationBlockDetailRow = {
  rowId: string;
  applicationNumber: string;
  approvalNumber: string;
  sellerName: string;
  buyerName: string;
  route: string;
  blockNumber: number;
  blockLabel: string;
  scheduledQuantum: number;
  actualQuantum: number;
  deviation: number;
};

type ApplicationEnergyFlowDay = {
  date: string;
  blocks: ApplicationBlockDetailRow[];
};

type ApplicationEnergyFlowModalData = {
  title: string;
  subtitle: string;
  days: ApplicationEnergyFlowDay[];
};

@Component({
  standalone: true,
  selector: 'app-grid-india-schedules-page',
  imports: [CommonModule, FormsModule, LucideAngularModule],
  template: `
    <div class="schedule-shell p-2 p-lg-2">
      <section class="hero-card">
        <div>
          <div class="eyebrow">Auto Fetch</div>
          <h1>Grid India Schedules</h1>
          <p>Fetch and store 96-block schedule data from Grid India using the configured backend source URL or a one-off custom export URL.</p>
        </div>
        <div class="hero-actions">
          <label class="field d-none">
            <span>Region</span>
            <select class="form-control" [ngModel]="form().region" (ngModelChange)="updateField('region', $event)">
              @for (region of regionOptions; track region) {
                <option [value]="region">{{ region }}</option>
              }
            </select>
          </label>
          <label class="field">
            <span>Schedule Date</span>
            <input class="form-control" type="date" [ngModel]="form().schedule_date" (ngModelChange)="updateField('schedule_date', $event)" />
          </label>
          <label class="field">
            <span>Revision Number</span>
            <select class="form-control" [ngModel]="form().revision_number" (ngModelChange)="updateField('revision_number', $event)">
              @for (revision of revisionOptions(); track revision) {
                <option [value]="revision">Revision {{ revision }}</option>
              }
            </select>
          </label>
          <label class="field field-url d-none">
            <span>Custom Source URL</span>
            <input class="form-control" type="text" placeholder="Optional override URL" [ngModel]="form().source_url" (ngModelChange)="updateField('source_url', $event)" />
          </label>
          <button type="button" class="toolbar-btn d-none" (click)="loadSnapshots()" [disabled]="loading()">
            <lucide-icon [img]="RefreshIcon" [size]="15"></lucide-icon>
            <span>Refresh</span>
          </button>
          <button type="button" class="fetch-btn d-none" (click)="fetchSnapshot(false)" [disabled]="fetching()">
            <lucide-icon [img]="DownloadIcon" [size]="15"></lucide-icon>
            <span>{{ fetching() ? 'Fetching...' : 'Fetch Schedule' }}</span>
          </button>
        </div>
      </section>

      <section class="info-strip d-none">
        <div class="info-card">
          <lucide-icon [img]="GlobeIcon" [size]="16"></lucide-icon>
          <span>Backend source: GRID_INDIA_SCHEDULE_URL or the custom URL entered above.</span>
        </div>
        <div class="info-card">
          <lucide-icon [img]="DatabaseIcon" [size]="16"></lucide-icon>
          <span>{{ snapshots().length }} stored snapshot{{ snapshots().length === 1 ? '' : 's' }} for the selected filter.</span>
        </div>
      </section>

      <section class="content-grid">
        <article class="panel-card detail-card">
          <div class="panel-head ">
            <div>
              <h2>Schedule Blocks</h2>
              <p>Stored 15-minute schedule values from the selected import.</p>
            </div>
            <div class="panel-actions ">
              <button type="button" class="toolbar-btn d-none" (click)="openSource(selectedSnapshot())" [disabled]="!selectedSnapshot()?.source_url">
                <lucide-icon [img]="GlobeIcon" [size]="14"></lucide-icon>
                <span>Open Source</span>
              </button>
              @if (selectedSnapshot(); as snapshot) {
                <button type="button" class="toolbar-btn d-none" (click)="refetchSnapshot(snapshot)" [disabled]="isRefetching(snapshot.id)">
                  <lucide-icon [img]="RefreshIcon" [size]="14"></lucide-icon>
                  <span>{{ isRefetching(snapshot.id) ? 'Re-fetching...' : 'Re-fetch' }}</span>
                </button>
              }
            </div>
          </div>

          @if (!selectedSnapshot()) {
            <div class="empty-state">Select a stored snapshot to inspect blockwise schedule data.</div>
          } @else {
            <div class="summary-strip d-none">
              <div><span>Region</span><strong>{{ selectedSnapshot()?.region }}</strong></div>
              <div><span>Date</span><strong>{{ selectedSnapshot()?.schedule_date }}</strong></div>
              <div><span>Blocks</span><strong>{{ selectedSnapshot()?.blocks?.length || 0 }}</strong></div>
              <div><span>Total MW</span><strong>{{ totalScheduleMw() | number:'1.0-2' }}</strong></div>
            </div>

	            <section class="applications-card">
              <div class="section-head">
                <div>
                  <h3>Today's Applications</h3>
                  <p>Applications mapped to the selected schedule date with generated dummy approval numbers where needed.</p>
                </div>
                <span class="section-count">{{ todayApplications().length }}</span>
              </div>
              <div class="table-wrap">
	                <table class="table-grid application-table">
	                  <thead>
	                    <tr>
	                      <th>Application No.</th>
	                      <th>Approval No.</th>
	                      <th>Seller</th>
	                      <th>Buyer</th>
	                      <th>Scheduled Quantum</th>
	                      <th>Actual Quantum</th>
	                      <th class="col-view">View</th>
	                    </tr>
	                  </thead>
	                  <tbody>
	                    @if (!todayApplications().length) {
	                      <tr>
	                        <td colspan="7" class="empty-cell">No applications found for {{ form().schedule_date }}.</td>
	                      </tr>
	                    } @else {
	                      @for (application of todayApplications(); track application.id) {
	                        <tr>
	                          <td>{{ application.applicationNumber }}</td>
	                          <td>{{ application.approvalNumber }}</td>
	                          <td>{{ application.sellerName }}</td>
	                          <td>{{ application.buyerName }}</td>
	                          <td>{{ application.scheduledQuantum | number:'1.0-2' }}</td>
	                          <td>{{ application.actualQuantum | number:'1.0-2' }}</td>
	                          <td class="col-view">
	                            <button type="button" class="icon-action-btn" title="View 96-block energy flow" (click)="openEnergyFlowModal(application)">
	                              <lucide-icon [img]="EyeIcon" [size]="16"></lucide-icon>
	                            </button>
	                          </td>
	                        </tr>
	                      }
	                    }
	                  </tbody>
	                </table>
	              </div>
	            </section>

	            <div class="raw-card d-none">
	              <div class="raw-head">
	                <h3>Raw Payload Preview</h3>
	                <span>{{ rawPreview().length }} chars shown</span>
	              </div>
	              <pre>{{ rawPreview() }}</pre>
	            </div>
	          }
	        </article>
	      </section>
	    </div>

	    @if (energyFlowModal(); as modal) {
	      <div class="modal-backdrop" (click)="closeEnergyFlowModal()"></div>
	      <div class="modal-shell" role="dialog" aria-modal="true">
	        <div class="modal-card" (click)="$event.stopPropagation()">
	          <div class="modal-head">
	            <div>
	              <h3 class="modal-title">{{ modal.title }}</h3>
	              <div class="modal-subtitle">{{ modal.subtitle }}</div>
	            </div>
	            <button type="button" class="modal-close" (click)="closeEnergyFlowModal()" title="Close">
	              <lucide-icon [img]="CloseIcon" [size]="18"></lucide-icon>
	            </button>
	          </div>

	          <div class="modal-body">
	            @if (!modal.days.length) {
	              <div class="empty-state">No schedules found for this application.</div>
	            } @else {
		              <div class="flow-table-wrap">
		                <table class="table-grid flow-table">
		                  <thead>
		                    <tr>
		                      <th>Application No.</th>
		                      <th>Approval No.</th>
		                      <th>Seller</th>
		                      <th>Buyer</th>
		                      <th>Route</th>
		                      <th>Block</th>
		                      <th>Time</th>
		                      <th>Scheduled Quantum</th>
		                      <th>Actual Quantum</th>
		                      <th>Deviation</th>
		                    </tr>
		                  </thead>
		                  <tbody>
		                    @for (day of modal.days; track day.date) {
		                      <tr class="date-row">
		                        <td colspan="10">{{ formatDateDdMmYyyy(day.date) }}</td>
		                      </tr>
		                      @for (row of day.blocks; track row.rowId) {
		                        <tr>
		                          <td>{{ row.applicationNumber }}</td>
		                          <td>{{ row.approvalNumber }}</td>
		                          <td>{{ row.sellerName }}</td>
		                          <td>{{ row.buyerName }}</td>
		                          <td>{{ row.route }}</td>
		                          <td>{{ row.blockNumber }}</td>
		                          <td>{{ row.blockLabel }}</td>
		                          <td>{{ row.scheduledQuantum | number:'1.0-2' }}</td>
		                          <td>{{ row.actualQuantum | number:'1.0-2' }}</td>
		                          <td [class.deviation-positive]="row.deviation > 0" [class.deviation-negative]="row.deviation < 0">{{ row.deviation | number:'1.0-2' }}</td>
		                        </tr>
		                      }
		                    }
		                  </tbody>
		                </table>
	              </div>
	            }
	          </div>
	        </div>
	      </div>
	    }
	  `,
  styles: [`
    .schedule-shell { display:grid; gap:1rem; color:#18324d; }
    .hero-card,.panel-card,.info-card,.raw-card{
      border:1px solid rgba(18,40,72,.08);
      border-radius:1rem;
      background:linear-gradient(180deg,#ffffff 0%,#f7fafc 100%);
      box-shadow:0 16px 35px rgba(15,23,42,.06);
    }
    .hero-card{display:flex;justify-content:space-between;gap:1rem;padding:1.15rem 1.2rem;align-items:flex-start;}
    .eyebrow{text-transform:uppercase;letter-spacing:.08em;font-size:.72rem;color:#6b7b8c;margin-bottom:.35rem;}
    .hero-card h1,.panel-head h2,.raw-head h3{margin:0;}
    .hero-card p,.panel-head p,.empty-state,.info-card span{color:#607286;}
    .hero-actions{display:flex;gap:.7rem;flex-wrap:wrap;justify-content:flex-end;}
    .field{display:grid;gap:.28rem;min-width:170px;}
    .field-url{min-width:320px;}
    .field span{font-size:.74rem;font-weight:700;color:#667a90;text-transform:uppercase;letter-spacing:.04em;}
    .form-control{min-height:42px;border-radius:.75rem;border:1px solid rgba(30,58,95,.16);padding:.65rem .8rem;}
    .toolbar-btn,.fetch-btn{
      display:inline-flex;align-items:center;gap:.45rem;border-radius:.75rem;padding:.72rem .95rem;
      font-size:.84rem;font-weight:700;border:1px solid rgba(27,63,102,.14);background:#fff;color:#23405e;
    }
    .fetch-btn{background:#0c4a91;color:#fff;border-color:#0c4a91;}
    .info-strip{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:1rem;}
    .info-card{display:flex;align-items:center;gap:.6rem;padding:.85rem 1rem;}
    .content-grid{display:grid;grid-template-columns:minmax(0,1fr);gap:1rem;}
    .panel-card{padding:1rem;}
    .detail-card{display:grid;gap:1rem;}
    .panel-head{display:flex;justify-content:space-between;align-items:flex-start;gap:1rem;margin-bottom:.85rem;}
    .panel-actions{display:flex;gap:.6rem;flex-wrap:wrap;}
    .snapshot-list{display:grid;gap:.7rem;max-height:720px;overflow:auto;}
    .snapshot-item{
      display:flex;justify-content:space-between;gap:.8rem;align-items:flex-start;width:100%;
      border:1px solid rgba(18,40,72,.08);border-radius:.85rem;background:#fff;padding:.9rem;text-align:left;
    }
    .snapshot-item.active{border-color:#2563eb;background:#eff6ff;}
    .snapshot-item strong{display:block;}
    .snapshot-item small{display:block;margin-top:.2rem;color:#6b7b8c;}
    .method-chip{padding:.2rem .5rem;border-radius:999px;background:#dbeafe;color:#1d4ed8;font-size:.72rem;font-weight:800;}
    .summary-strip{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:.8rem;}
    .summary-strip div{padding:.8rem .9rem;border-radius:.8rem;background:#f8fafc;border:1px solid rgba(18,40,72,.06);}
    .summary-strip span{display:block;font-size:.74rem;color:#6b7b8c;text-transform:uppercase;letter-spacing:.04em;}
    .summary-strip strong{display:block;margin-top:.35rem;font-size:1.1rem;}
    .applications-card{display:grid;gap:.75rem;}
    .section-head{display:flex;align-items:flex-start;justify-content:space-between;gap:1rem;}
    .section-actions{display:flex;align-items:center;gap:.6rem;}
    .section-head h3{margin:0;font-size:1.02rem;}
    .section-head p{margin:.2rem 0 0;color:#607286;}
    .section-count{min-width:2rem;height:2rem;padding:0 .65rem;border-radius:999px;background:#e8f1ff;color:#174ea6;font-weight:800;display:inline-flex;align-items:center;justify-content:center;}
    .icon-action-btn{width:2rem;height:2rem;border:1px solid rgba(18,40,72,.12);border-radius:.6rem;background:#fff;color:#174ea6;display:inline-flex;align-items:center;justify-content:center;}
	    .table-wrap{overflow:auto;border:1px solid rgba(18,40,72,.08);border-radius:.9rem;}
	    .table-grid{width:100%;border-collapse:collapse;min-width:560px;}
	    .table-grid th,.table-grid td{padding:.78rem .8rem;border-bottom:1px solid rgba(18,40,72,.08);text-align:left;white-space:nowrap;}
	    .col-view{width:84px;text-align:center;}
	    td.col-view{text-align:center;}
	    .empty-cell{text-align:center;color:#607286;padding:1.2rem;}
	    .table-grid th{background:#f8fafc;color:#64748b;font-size:.78rem;text-transform:uppercase;letter-spacing:.05em;}
    .raw-card{padding:0;border:1px solid rgba(18,40,72,.08);}
    .raw-head{display:flex;justify-content:space-between;align-items:center;padding:.9rem 1rem;border-bottom:1px solid rgba(18,40,72,.08);}
    .raw-head span{font-size:.78rem;color:#6b7b8c;}
	    pre{margin:0;padding:1rem;max-height:280px;overflow:auto;background:#0f172a;color:#e2e8f0;border-radius:0 0 1rem 1rem;font-size:.78rem;line-height:1.45;}
	    .empty-state{padding:2rem .5rem;text-align:center;}
	    .modal-backdrop{position:fixed;left:0;right:0;top:46px;bottom:0;background:rgba(15,23,42,.55);z-index:2000;}
	    .modal-shell{position:fixed;left:0;right:0;top:46px;bottom:0;display:grid;place-items:center;padding:1rem;z-index:2010;}
	    .modal-card{
	      width:min(1480px, calc(100vw - 2rem));
	      max-height:calc(100vh - 46px - 2rem);
	      overflow:hidden;
	      border:1px solid rgba(18,40,72,.12);
	      border-radius:1rem;
	      background:linear-gradient(180deg,#ffffff 0%,#f7fafc 100%);
	      box-shadow:0 22px 50px rgba(15,23,42,.18);
	      display:grid;
	      grid-template-rows:auto 1fr;
	    }
	    .modal-head{display:flex;align-items:flex-start;justify-content:space-between;gap:1rem;padding:1rem 1.1rem;border-bottom:1px solid rgba(18,40,72,.08);}
	    .modal-title{margin:0;font-size:1.05rem;}
	    .modal-subtitle{margin-top:.2rem;color:#607286;font-size:.85rem;}
	    .modal-close{width:2.35rem;height:2.35rem;border:1px solid rgba(18,40,72,.12);border-radius:.75rem;background:#fff;color:#174ea6;display:inline-flex;align-items:center;justify-content:center;}
	    .modal-body{padding:1rem;overflow:auto;}
	    .flow-table{min-width:1200px;}
	    .flow-table-wrap{overflow:auto;border:1px solid rgba(18,40,72,.08);border-radius:.9rem;}
	    .date-row td{background:#eff6ff;color:#1d4ed8;font-weight:800;text-transform:uppercase;letter-spacing:.04em;font-size:.76rem;}
	    .deviation-positive{color:#047857;font-weight:800;}
	    .deviation-negative{color:#b42318;font-weight:800;}
	    @media (max-width: 575.98px){
	      .modal-backdrop{top:44px;}
	      .modal-shell{top:44px;}
	      .modal-card{max-height:calc(100vh - 44px - 2rem);}
	    }
	    @media (max-width: 1100px){
	      .content-grid,.info-strip,.summary-strip{grid-template-columns:1fr;}
	      .field-url{min-width:220px;}
	    }
    @media (max-width: 820px){
      .hero-card{flex-direction:column;}
      .hero-actions{justify-content:flex-start;}
    }
  `],
})
export class GridIndiaSchedulesPage implements OnInit {
  private readonly api = inject(ApiService);
  private readonly vendorsService = inject(VendorsService);
  private readonly toast = inject(ToastService);

  protected readonly regionOptions = ['NLDC', 'NRLDC', 'WRLDC', 'SRLDC', 'ERLDC', 'NERLDC'] as const;
  protected readonly revisionOptions = computed(() => this.buildRevisionOptions(this.form().schedule_date));
  protected readonly CalendarRangeIcon = CalendarRange;
  protected readonly DatabaseIcon = Database;
	  protected readonly DownloadIcon = Download;
	  protected readonly EyeIcon = Eye;
	  protected readonly GlobeIcon = Globe;
	  protected readonly RefreshIcon = RefreshCcw;
	  protected readonly CloseIcon = X;

	  protected readonly loading = signal(false);
	  protected readonly fetching = signal(false);
  protected readonly refetchingById = signal<Record<string, boolean>>({});
  protected readonly snapshots = signal<GridIndiaScheduleSnapshot[]>([]);
  protected readonly applications = signal<ApplicationApi[]>([]);
  protected readonly vendors = signal<Vendor[]>([]);
  protected readonly selectedSnapshot = signal<GridIndiaScheduleSnapshotDetail | null>(null);
  protected readonly activeSnapshotId = signal<string | null>(null);
  private readonly snapshotDetails = signal<Record<string, GridIndiaScheduleSnapshotDetail>>({});
	  protected readonly vendorMap = computed(() => new Map(this.vendors().map((vendor) => [vendor.id, vendor])));
	  protected readonly energyFlowModal = signal<ApplicationEnergyFlowModalData | null>(null);
  protected readonly form = signal({
    region: 'NLDC',
    schedule_date: this.todayValue(),
    revision_number: this.defaultRevisionForDate(this.todayValue()),
    source_url: '',
  });
  protected readonly totalScheduleMw = computed(() =>
    (this.selectedSnapshot()?.blocks || []).reduce((sum, block) => sum + (Number(block.schedule_mw) || 0), 0),
  );

	  protected readonly todayApplications = computed<TodayApplicationRow[]>(() => {
	    const targetDate = this.form().schedule_date;
	    const vendorMap = this.vendorMap();

	    return this.applications()
	      .filter((application) => this.isApplicationOnDate(application, targetDate))
	      .map((application) => {
	        const totalPunched = Number(application.total_quantum);
	        const punchedQuantum = Number.isFinite(totalPunched) && totalPunched > 0 ? totalPunched : this.applicationQuantum(application);
	        const approvedQuantum = Number(application.approved_quantum);
	        const approvedAvailable = Number.isFinite(approvedQuantum) && approvedQuantum > 0;
	        const revisionNumber = String(this.form().revision_number || '0');
	        return {
	          id: application.id,
	          applicationNumber: application.application_number || '-',
	          approvalNumber: this.resolveApprovalNumber(application),
	          sellerName: application.seller_vendor_id ? vendorMap.get(String(application.seller_vendor_id))?.vendor_name || '-' : '-',
	          buyerName: application.buyer_vendor_id ? vendorMap.get(String(application.buyer_vendor_id))?.vendor_name || '-' : '-',
	          scheduledQuantum: Number(punchedQuantum.toFixed(2)),
		          actualQuantum: approvedAvailable
		            ? Number(approvedQuantum.toFixed(2))
		            : punchedQuantum > 0
		              ? this.randomizeWithinPercent(punchedQuantum, 0.05, `${application.id}|${targetDate}|r${revisionNumber}`)
		              : 0,
	        };
	      })
	      .sort((left, right) => left.applicationNumber.localeCompare(right.applicationNumber));
	  });
	  protected readonly rawPreview = computed(() => (this.selectedSnapshot()?.raw_payload_text || '').slice(0, 4000));

  ngOnInit(): void {
    this.loadSnapshots();
    this.loadApplications();
  }

  protected updateField(field: 'region' | 'schedule_date' | 'revision_number' | 'source_url', value: string): void {
    if (field === 'schedule_date') {
      const nextDate = value || '';
      this.form.update((current) => ({
        ...current,
        schedule_date: nextDate,
        revision_number: this.defaultRevisionForDate(nextDate),
      }));
      this.loadSnapshots();
      return;
    }

    this.form.update((current) => ({ ...current, [field]: value || '' }));
    if (field === 'region') {
      this.loadSnapshots();
    }
  }

  protected loadSnapshots(): void {
    const payload = this.form();
    this.loading.set(true);
    const details = this.buildDummySnapshotDetails(payload.region, payload.schedule_date, payload.source_url);
    const detailMap = Object.fromEntries(details.map((detail) => [detail.id, detail]));
    const rows = details.map(({ blocks, raw_payload_text, ...snapshot }) => snapshot);
    this.snapshotDetails.set(detailMap);
    this.snapshots.set(rows);
    this.loading.set(false);

    const requestedId = this.activeSnapshotId();
    const currentId = requestedId && detailMap[requestedId] ? requestedId : rows[0]?.id || null;
    if (currentId) {
      this.selectSnapshot(currentId);
    } else {
      this.activeSnapshotId.set(null);
      this.selectedSnapshot.set(null);
    }
  }

  protected fetchSnapshot(forceRefetch = false): void {
    const payload = this.form();
    this.fetching.set(true);
    const detail = this.buildFetchedSnapshotDetail(payload.region, payload.schedule_date, payload.source_url, forceRefetch);
    const { blocks, raw_payload_text, ...snapshot } = detail;
    this.snapshotDetails.set({ [detail.id]: detail, ...this.snapshotDetails() });
    this.snapshots.set([snapshot, ...this.snapshots().filter((item) => item.id !== detail.id)]);
    this.fetching.set(false);
    this.activeSnapshotId.set(detail.id);
    this.selectedSnapshot.set(detail);
    this.toast.show(`Dummy Grid India schedule ready for ${detail.region} on ${detail.schedule_date}`, '', 'success');
  }

  protected selectSnapshot(snapshotId: string): void {
    this.activeSnapshotId.set(snapshotId);
    this.selectedSnapshot.set(this.snapshotDetails()[snapshotId] || null);
  }

  protected refetchSnapshot(snapshot: GridIndiaScheduleSnapshotDetail): void {
    this.refetchingById.set({ ...this.refetchingById(), [snapshot.id]: true });
    const detail = this.buildRefetchedSnapshotDetail(snapshot);
    const { blocks, raw_payload_text, ...row } = detail;
    this.snapshotDetails.set({ ...this.snapshotDetails(), [detail.id]: detail });
    this.snapshots.set(this.snapshots().map((item) => (item.id === detail.id ? row : item)));
    this.selectedSnapshot.set(detail);
    this.activeSnapshotId.set(detail.id);
    this.refetchingById.set({ ...this.refetchingById(), [snapshot.id]: false });
    this.toast.show('Dummy Grid India schedule refreshed', '', 'success');
  }

  protected isRefetching(snapshotId: string): boolean {
    return !!this.refetchingById()[snapshotId];
  }

  protected openSource(snapshot: GridIndiaScheduleSnapshotDetail | null): void {
    if (!snapshot?.source_url) return;
    window.open(snapshot.source_url, '_blank', 'noopener');
  }

  private buildRevisionOptions(scheduleDate: string): string[] {
    const latestRevision = this.latestRevisionForDate(scheduleDate);
    return Array.from({ length: latestRevision + 1 }, (_, index) => String(index));
  }

  private defaultRevisionForDate(scheduleDate: string): string {
    return String(this.latestRevisionForDate(scheduleDate));
  }

  private latestRevisionForDate(scheduleDate: string): number {
    const normalizedTarget = this.normalizeDateValue(scheduleDate);
    const today = this.normalizeDateValue(this.todayValue());
    if (!normalizedTarget) return 0;
    if (normalizedTarget < today) return 95;
    if (normalizedTarget > today) return 0;

    const now = new Date();
    const minutes = now.getHours() * 60 + now.getMinutes();
    return Math.min(95, Math.floor(minutes / 15));
  }

  private loadApplications(): void {
    this.api.get<ApplicationApi[]>('/api/applications').subscribe({
      next: (data) => this.applications.set(data || []),
      error: () => this.applications.set([]),
    });
    this.vendorsService.list().subscribe({
      next: (data) => this.vendors.set(data || []),
      error: () => this.vendors.set([]),
    });
  }

  private isApplicationOnDate(application: ApplicationApi, targetDate: string): boolean {
    if (!targetDate) return false;
    const schedules = application.schedules || [];
    if (!schedules.length) return this.normalizeDateValue(application.application_date) === targetDate;
    return schedules.some((schedule) => {
      const from = this.normalizeDateValue(schedule.from_date);
      const to = this.normalizeDateValue(schedule.to_date);
      if (!from || !to) return false;
      return targetDate >= from && targetDate <= to;
    });
  }

	  private applicationQuantum(application: ApplicationApi): number {
	    const scheduled = (application.schedules || []).reduce((sum, row) => sum + (Number(row.quantum) || 0), 0);
	    return scheduled || Number(application.total_quantum) || Number(application.approved_quantum) || 0;
	  }

	  protected openEnergyFlowModal(row: TodayApplicationRow): void {
	    const application = this.applications().find((item) => item.id === row.id) || null;
	    if (!application) return;
	    const days = this.buildEnergyFlowDays(application);
	    this.energyFlowModal.set({
	      title: 'Application 96-Block Energy Flow',
	      subtitle: `${row.applicationNumber} • ${row.sellerName} → ${row.buyerName}`,
	      days,
	    });
	  }

	  protected closeEnergyFlowModal(): void {
	    this.energyFlowModal.set(null);
	  }

		  private buildEnergyFlowDays(application: ApplicationApi): ApplicationEnergyFlowDay[] {
		    const snapshotBlocks = this.selectedSnapshot()?.blocks || [];
		    if (!snapshotBlocks.length) return [];

		    const schedules = application.schedules || [];
		    const normalizedSchedules = schedules
		      .map((schedule) => ({
		        from: this.normalizeDateValue(schedule.from_date),
		        to: this.normalizeDateValue(schedule.to_date),
		        fromMinutes: this.parseTimeToMinutes(schedule.from_time),
		        toMinutes: this.parseTimeToMinutes(schedule.to_time),
		        quantum: Number(schedule.quantum) || 0,
		      }))
		      .filter((row) => !!row.from && !!row.to);

		    const fromDate = normalizedSchedules.reduce((min, row) => (!min || row.from < min ? row.from : min), '');
		    const toDate = normalizedSchedules.reduce((max, row) => (!max || row.to > max ? row.to : max), '');
		    const today = this.todayValue();
		    const cappedToDate = toDate && toDate > today ? today : toDate;
		    const range = fromDate && cappedToDate ? this.enumerateDates(fromDate, cappedToDate) : [];
		    const dates = range.length ? range : [this.normalizeDateValue(application.application_date)].filter((d) => !!d);

		    const vendorMap = this.vendorMap();
		    const sellerName = application.seller_vendor_id ? vendorMap.get(String(application.seller_vendor_id))?.vendor_name || '-' : '-';
		    const buyerName = application.buyer_vendor_id ? vendorMap.get(String(application.buyer_vendor_id))?.vendor_name || '-' : '-';
		    const approvalNumber = this.resolveApprovalNumber(application);
		    const route = this.routeLabel(application.route);

			    const totalPunched = Number(application.total_quantum);
			    const punchedQuantum = Number.isFinite(totalPunched) && totalPunched > 0 ? totalPunched : this.applicationQuantum(application);
			    const totalApproved = Number(application.approved_quantum);
			    const approvedAvailable = Number.isFinite(totalApproved) && totalApproved > 0;
			    const approvalRatio = approvedAvailable && punchedQuantum > 0 ? totalApproved / punchedQuantum : null;
			    const revisionNumber = Number(this.form().revision_number || '0');

		    const blocksWithMinutes = snapshotBlocks
		      .map((block) => {
		        const minutes = this.blockMinutesFromLabel(block.block_label);
		        return minutes ? { ...block, ...minutes } : null;
		      })
		      .filter((item): item is GridIndiaScheduleBlock & { startMinutes: number; endMinutes: number } => item !== null);

		    return [...dates]
		      .filter((date) => date <= today)
		      .sort((a, b) => b.localeCompare(a))
			      .map((date) => {
			        const rows = blocksWithMinutes.map((block) => {
			          const scheduledQuantum = this.scheduledQuantumForBlock(normalizedSchedules, date, block.startMinutes, block.endMinutes);
			          const actualQuantum =
			            scheduledQuantum <= 0
			              ? 0
			              : approvalRatio !== null
			                ? Number((scheduledQuantum * approvalRatio).toFixed(2))
			                : this.randomizeWithinPercent(
			                    scheduledQuantum,
			                    0.05,
			                    `${application.id}|${date}|${block.block_number}|r${revisionNumber}`,
			                  );
			          const deviation = Number((actualQuantum - scheduledQuantum).toFixed(2));
			          return {
			            rowId: `${date}-${application.id}-${block.block_number}`,
		            applicationNumber: application.application_number || '-',
		            approvalNumber,
		            sellerName,
		            buyerName,
		            route,
		            blockNumber: block.block_number,
		            blockLabel: block.block_label,
		            scheduledQuantum,
		            actualQuantum,
		            deviation,
		          };
		        });
		        return { date, blocks: rows };
		      })
		      .filter((day) => day.blocks.some((row) => row.scheduledQuantum > 0));
		  }

		  private scheduledQuantumForBlock(
		    schedules: Array<{
		      from: string;
		      to: string;
		      fromMinutes: number | null;
		      toMinutes: number | null;
		      quantum: number;
		    }>,
		    date: string,
		    blockStartMinutes: number,
		    blockEndMinutes: number,
		  ): number {
		    const scheduled = schedules.reduce((sum, schedule) => {
		      if (date < schedule.from || date > schedule.to) return sum;
		      if (schedule.fromMinutes === null || schedule.toMinutes === null) return sum;
		      if (schedule.quantum <= 0) return sum;
		      if (blockStartMinutes < schedule.fromMinutes || blockEndMinutes > schedule.toMinutes) return sum;
		      return sum + schedule.quantum;
		    }, 0);
		    return Number((scheduled || 0).toFixed(2));
		  }

	  private enumerateDates(fromYmd: string, toYmd: string): string[] {
	    const start = new Date(fromYmd);
	    const end = new Date(toYmd);
	    if (!Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime())) return [];
	    if (end.getTime() < start.getTime()) return [];

	    const dates: string[] = [];
	    const cursor = new Date(Date.UTC(start.getFullYear(), start.getMonth(), start.getDate()));
	    const endUtc = Date.UTC(end.getFullYear(), end.getMonth(), end.getDate());
	    while (cursor.getTime() <= endUtc) {
	      dates.push(cursor.toISOString().slice(0, 10));
	      cursor.setUTCDate(cursor.getUTCDate() + 1);
	    }
	    return dates;
	  }

		  private parseTimeToMinutes(value: string | null | undefined): number | null {
	    if (!value) return null;
	    const match = /^(\d{2}):(\d{2})$/.exec(String(value).trim());
	    if (!match) return null;
	    const hours = Number(match[1]);
	    const minutes = Number(match[2]);
	    if (!Number.isInteger(hours) || !Number.isInteger(minutes) || minutes < 0 || minutes >= 60) return null;
	    if (hours === 24 && minutes === 0) return 24 * 60;
	    if (hours < 0 || hours > 23) return null;
		    return hours * 60 + minutes;
		  }

		  private blockMinutesFromLabel(label: string | null | undefined): { startMinutes: number; endMinutes: number } | null {
		    const raw = String(label || '').trim();
		    if (!raw) return null;
		    const parts = raw.split('-').map((item) => item.trim());
		    if (parts.length !== 2) return null;
		    const start = this.parseTimeToMinutes(parts[0]);
		    const end = this.parseTimeToMinutes(parts[1]);
		    if (start === null || end === null) return null;
		    return { startMinutes: start, endMinutes: end };
		  }

  private resolveApprovalNumber(application: ApplicationApi): string {
    const existing = String(application.approval_number || '').trim();
    if (existing) return existing;
    const appNo = (application.application_number || 'APP').replace(/\s+/g, '-');
    const revision = this.form().revision_number || '0';
    return `${appNo}-/R${revision}`;
  }

		  private normalizeDateValue(value: string | null | undefined): string {
	    if (!value) return '';
	    const date = new Date(value);
	    if (Number.isNaN(date.getTime())) return value.slice(0, 10);
	    return date.toISOString().slice(0, 10);
		  }

	  protected formatDateDdMmYyyy(value: string | null | undefined): string {
	    const raw = String(value || '').trim();
	    if (!raw) return '-';
	    const [yyyy, mm, dd] = raw.slice(0, 10).split('-');
	    if (!yyyy || !mm || !dd) return raw;
	    return `${dd}-${mm}-${yyyy}`;
	  }

		  private randomizeWithinPercent(value: number, percent: number, seed: string): number {
		    const base = Number(value);
		    if (!Number.isFinite(base) || base <= 0) return 0;
		    const pct = Math.min(Math.max(Number(percent), 0), 1);
		    const rand = this.seededRandom(seed); // 0..1
		    const delta = (rand * 2 - 1) * pct; // -pct..+pct
		    const next = base * (1 + delta);
		    return Number(Math.max(0, next).toFixed(2));
		  }

		  private seededRandom(seed: string): number {
		    // FNV-1a hash to 32-bit int, then map to 0..1.
		    let hash = 2166136261;
		    const text = String(seed || '');
		    for (let index = 0; index < text.length; index += 1) {
		      hash ^= text.charCodeAt(index);
		      hash = Math.imul(hash, 16777619);
		    }
		    const unsigned = hash >>> 0;
		    return unsigned / 4294967295;
		  }

	  private routeLabel(value: string | null | undefined): string {
	    return (value || '').split(',').map((item) => item.trim()).filter((item) => !!item).join(', ') || '-';
	  }

  protected formatDateTime(value: string | null | undefined): string {
    if (!value) return '-';
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
  }

  private todayValue(): string {
    const now = new Date();
    const month = `${now.getMonth() + 1}`.padStart(2, '0');
    const day = `${now.getDate()}`.padStart(2, '0');
    return `${now.getFullYear()}-${month}-${day}`;
  }

  private buildDummySnapshotDetails(region: string, scheduleDate: string, sourceUrl: string): GridIndiaScheduleSnapshotDetail[] {
    return [
      this.createSnapshotDetail(region, scheduleDate, 'auto-primary', sourceUrl, 'GET', 0),
      this.createSnapshotDetail(region, scheduleDate, 'auto-revision', sourceUrl, 'POST', 1),
      this.createSnapshotDetail(region, scheduleDate, 'manual-upload', sourceUrl, 'POST', 2),
    ];
  }

  private buildFetchedSnapshotDetail(
    region: string,
    scheduleDate: string,
    sourceUrl: string,
    forceRefetch: boolean,
  ): GridIndiaScheduleSnapshotDetail {
    return this.createSnapshotDetail(region, scheduleDate, forceRefetch ? 'forced-refetch' : 'manual-fetch', sourceUrl, 'POST', 3);
  }

  private buildRefetchedSnapshotDetail(snapshot: GridIndiaScheduleSnapshotDetail): GridIndiaScheduleSnapshotDetail {
    const detail = this.createSnapshotDetail(snapshot.region, snapshot.schedule_date, 'refetched', snapshot.source_url, 'POST', 4);
    return {
      ...detail,
      id: snapshot.id,
      report_key: snapshot.report_key,
      created_at: snapshot.created_at,
      last_refetched_at: this.buildTimestamp(snapshot.schedule_date, 5, 45),
    };
  }

  private createSnapshotDetail(
    region: string,
    scheduleDate: string,
    variant: string,
    sourceUrl: string,
    requestMethod: string,
    seedOffset: number,
  ): GridIndiaScheduleSnapshotDetail {
    const blocks = this.buildBlocks(region, scheduleDate, seedOffset);
    const fetchedAt = this.buildTimestamp(scheduleDate, 6 + seedOffset, 10);
    const lastRefetchedAt = seedOffset === 0 ? null : this.buildTimestamp(scheduleDate, 7 + seedOffset, 25);
    const resolvedSourceUrl = sourceUrl.trim() || `https://grid-india.example/${region.toLowerCase()}/${scheduleDate}/${variant}.xlsx`;
    const reportKey = `${region}-${scheduleDate}-${variant}`;
    return {
      id: reportKey,
      report_key: reportKey,
      region,
      schedule_date: scheduleDate,
      source_url: resolvedSourceUrl,
      request_method: requestMethod,
      row_count: blocks.length,
      fetched_at: fetchedAt,
      last_refetched_at: lastRefetchedAt,
      created_at: fetchedAt,
      blocks,
      raw_payload_text: JSON.stringify(
        {
          report_key: reportKey,
          region,
          schedule_date: scheduleDate,
          source_url: resolvedSourceUrl,
          blocks: blocks.slice(0, 12),
        },
        null,
        2,
      ),
    };
  }

  private buildBlocks(region: string, scheduleDate: string, seedOffset: number): GridIndiaScheduleBlock[] {
    const regionFactor = this.regionOptions.indexOf(region as (typeof this.regionOptions)[number]) + 1 || 1;
    const daySeed = Number(scheduleDate.split('-')[2] || '1');
    return Array.from({ length: 96 }, (_, index) => {
      const blockNumber = index + 1;
      const hour = Math.floor(index / 4);
      const minute = (index % 4) * 15;
      const endMinuteTotal = minute + 15;
      const endHour = endMinuteTotal >= 60 ? hour + 1 : hour;
      const normalizedEndHour = endHour % 24;
      const normalizedEndMinute = endMinuteTotal % 60;
      const base = 178 + regionFactor * 11 + seedOffset * 7 + daySeed * 0.8;
      const wave = Math.sin((blockNumber / 96) * Math.PI * 2) * 34;
      const shoulder = hour >= 18 && hour <= 22 ? 28 : hour >= 6 && hour <= 9 ? 18 : 0;
      const scheduleMw = Number((base + wave + shoulder + (blockNumber % 5) * 1.35).toFixed(2));
      const sourceValue = Number((scheduleMw + (blockNumber % 2 === 0 ? 0.42 : -0.37)).toFixed(2));
      return {
        block_number: blockNumber,
        block_label: `${this.formatTime(hour, minute)} - ${this.formatTime(normalizedEndHour, normalizedEndMinute)}`,
        schedule_mw: scheduleMw,
        source_value: sourceValue,
      };
    });
  }

  private buildTimestamp(scheduleDate: string, hour: number, minute: number): string {
    return `${scheduleDate}T${this.padTime(hour % 24)}:${this.padTime(minute)}:00`;
  }

  private formatTime(hour: number, minute: number): string {
    return `${this.padTime(hour)}:${this.padTime(minute)}`;
  }

  private padTime(value: number): string {
    return `${value}`.padStart(2, '0');
  }
}
